/* ==========================================================================
   Catalog Page (PORTAL-116)
   Read-only view of the products a provider can prescribe, grouped by
   category, without having to start a prescription. The backend already
   scopes the list: a provider with a custom catalog only receives their
   assigned products, everyone else receives the full active catalog.
   ========================================================================== */

import { useState, useEffect, useCallback, useMemo, type ReactNode } from 'react'
import { PageContainer } from '@/components/layout/PageContainer'
import { ProviderTabBar } from '@/components/layout/ProviderTabBar'
import { ProviderPageHeader } from '@/components/layout/ProviderPageHeader'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { usePortal, usePortalSlug } from '@/context/PortalContext'
import { getCatalog } from '@/api/portal'
import type { CatalogProductForPortal } from '@/api/types'
import { compareCategories, UNCATEGORIZED_LABEL } from '@/utils/sort'
import { drugCodeLabel } from '@/utils/countries'
import { QuantityDisplay } from '@/components/forms/PrescriptionDetails'
import { Package, RefreshCw, Search } from 'lucide-react'

/* --------------------------------------------------------------------------
   Helpers
   -------------------------------------------------------------------------- */

/**
 * Group products by their full category list (joined), matching the public
 * landing page — multi-category products show one combined header. Products
 * are not re-sorted inside a group: the backend already orders them by drug
 * name, then numeric strength.
 */
function groupByCategory(
  products: CatalogProductForPortal[]
): Record<string, CatalogProductForPortal[]> {
  const groups: Record<string, CatalogProductForPortal[]> = {}
  for (const product of products) {
    const category = product.categories.join(' / ') || UNCATEGORIZED_LABEL
    if (!groups[category]) {
      groups[category] = []
    }
    groups[category].push(product)
  }
  return groups
}

/** Case-insensitive match on name, code, ingredients or category. */
function matchesQuery(
  product: CatalogProductForPortal,
  query: string
): boolean {
  const haystack = [
    product.display_name,
    product.product_code,
    product.ingredients ?? '',
    product.categories.join(' '),
  ]
    .join(' ')
    .toLowerCase()
  return haystack.includes(query)
}

function CatalogSkeleton() {
  return (
    <div className="animate-pulse space-y-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="h-24 bg-[var(--color-gray-100)] rounded" />
      ))}
    </div>
  )
}

function Field({
  label,
  value,
  className = '',
}: {
  label: string
  value: ReactNode
  className?: string
}) {
  return (
    <div className={className}>
      <dt className="text-sm font-medium text-[var(--color-gray-500)] break-words">
        {label}
      </dt>
      <dd className="mt-1 text-base text-[var(--color-charcoal)] break-words">
        {value}
      </dd>
    </div>
  )
}

function ProductCard({
  product,
  codeLabel,
}: {
  product: CatalogProductForPortal
  codeLabel: string
}) {
  return (
    <Card>
      <h3 className="font-semibold text-[var(--color-charcoal)] break-words">
        {product.display_name}
        {product.strength && (
          <span className="ml-2 text-sm font-normal text-[var(--color-gray-500)]">
            {product.strength}
          </span>
        )}
      </h3>
      <dl className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Product Code" value={product.product_code} />
        {product.ndc && <Field label={codeLabel} value={product.ndc} />}
        {product.ingredients && (
          <Field
            label="Ingredients"
            value={product.ingredients}
            className="sm:col-span-2"
          />
        )}
        <Field
          label="Directions"
          value={product.dose_specific_directions || '—'}
          className="sm:col-span-2"
        />
        {product.default_additional_directions && (
          <Field
            label="Additional Directions"
            value={product.default_additional_directions}
            className="sm:col-span-2"
          />
        )}
        {/* Same renderer as the prescription summary, so pack units show
            as "2*60g" and bare numeric units show just the count. */}
        <Field
          label="Quantity"
          value={
            product.quantity ? (
              <QuantityDisplay
                quantity={product.quantity}
                unitAmount={product.unit_amount}
              />
            ) : (
              '—'
            )
          }
        />
        <Field label="Days Supply" value={product.days_supply ?? '—'} />
        <Field label="Refills" value={product.refills ?? '—'} />
        {product.medical_necessity_statement && (
          <Field
            label="Medical Necessity"
            value={product.medical_necessity_statement}
            className="sm:col-span-2"
          />
        )}
      </dl>
    </Card>
  )
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function Catalog() {
  const slug = usePortalSlug()
  const { config } = usePortal()
  const codeLabel = drugCodeLabel(config?.country)

  const [products, setProducts] = useState<CatalogProductForPortal[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')

  const fetchCatalog = useCallback(
    async (forceRefresh = false) => {
      setLoading(true)
      setError(null)
      try {
        const data = await getCatalog(slug, forceRefresh)
        setProducts(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load catalog.')
      } finally {
        setLoading(false)
      }
    },
    [slug]
  )

  useEffect(() => {
    fetchCatalog()
  }, [fetchCatalog])

  const { filtered, groups, categories } = useMemo(() => {
    const normalized = query.trim().toLowerCase()
    const filtered = normalized
      ? products.filter((p) => matchesQuery(p, normalized))
      : products
    const groups = groupByCategory(filtered)
    const categories = Object.keys(groups).sort(compareCategories)
    return { filtered, groups, categories }
  }, [products, query])

  // A catalog with no categories at all shouldn't show a lone header.
  const showHeaders = !(
    categories.length === 1 && categories[0] === UNCATEGORIZED_LABEL
  )

  return (
    <PageContainer size="md" className="py-8">
      <ProviderPageHeader />
      <ProviderTabBar />

      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-charcoal)]">
            Catalog
          </h1>
          <p className="text-sm text-[var(--color-gray-500)] mt-1">
            Products you can prescribe through this pharmacy. Open New
            Prescription to order one.
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => fetchCatalog(true)}
          disabled={loading}
          aria-label="Refresh"
        >
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>

      {loading ? (
        <Card>
          <CatalogSkeleton />
        </Card>
      ) : error ? (
        <Card>
          <div className="py-8 text-center">
            <p className="text-[var(--color-error)] mb-4">{error}</p>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => fetchCatalog(true)}
            >
              Try Again
            </Button>
          </div>
        </Card>
      ) : products.length === 0 ? (
        <Card>
          <div className="py-12 text-center">
            <Package className="h-12 w-12 text-[var(--color-gray-400)] mx-auto mb-3" />
            <p className="text-[var(--color-gray-600)] font-medium">
              No products available
            </p>
            <p className="text-sm text-[var(--color-gray-500)] mt-1">
              Your pharmacy hasn’t made any products available to you yet.
            </p>
          </div>
        </Card>
      ) : (
        <>
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
              aria-hidden="true"
            />
            <Input
              type="search"
              placeholder="Search by name, code, ingredient or category"
              aria-label="Search catalog"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <p className="text-sm text-[var(--color-gray-500)] mt-2 mb-6">
            Showing {filtered.length} of {products.length} products
          </p>

          {filtered.length === 0 ? (
            <Card>
              <div className="py-12 text-center">
                <p className="text-[var(--color-gray-600)] font-medium">
                  No products match “{query.trim()}”
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setQuery('')}
                  className="mt-3"
                >
                  Clear search
                </Button>
              </div>
            </Card>
          ) : (
            <div className="space-y-10">
              {categories.map((category) => (
                <section key={category} aria-label={category}>
                  {showHeaders && (
                    <h2 className="text-lg font-semibold text-[var(--color-charcoal)] mb-4 border-b border-[var(--color-gray-200)] pb-2">
                      {category}
                    </h2>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {groups[category].map((product) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        codeLabel={codeLabel}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </>
      )}
    </PageContainer>
  )
}

export default Catalog
